import { Star } from 'lucide-react';
import { useState } from 'react';

interface StarInputProps {
  value: number;
  onChange: (value: number) => void;
  /** Accessible group label. */
  label: string;
}

/**
 * Interactive 1–5 star rating input — 40px gold stars in 44px tap targets
 * (handoff component sheet). A radiogroup of star buttons: pointer, hover
 * preview, and keyboard (Tab + Space/Enter) all work. Empty stars are muted
 * outlines, filled stars are gold — so the value reads without color alone.
 */
export function StarInput({ value, onChange, label }: StarInputProps): JSX.Element {
  const [hover, setHover] = useState(0);
  const shown = hover || value;

  return (
    <div className="star-input" role="radiogroup" aria-label={label}>
      {[1, 2, 3, 4, 5].map((n) => {
        const on = n <= shown;
        return (
          <button
            key={n}
            type="button"
            role="radio"
            aria-checked={value === n}
            aria-label={String(n)}
            className={`star-input__star${on ? ' star-input__star--on' : ''}`}
            onClick={() => onChange(n)}
            onMouseEnter={() => setHover(n)}
            onMouseLeave={() => setHover(0)}
            onFocus={() => setHover(n)}
            onBlur={() => setHover(0)}
          >
            <Star fill={on ? 'currentColor' : 'none'} />
          </button>
        );
      })}
    </div>
  );
}
